<template>
  <div>
    <div class="required-notice">빨간별표 표시는 필수 입력사항입니다.</div>

    <!-- 저장 확인 팝업 -->
    <vs-popup title="저장" :active.sync="showSaveConfirm">
      <p class="save-confirm-text">
        <span class="save-confirm-highlight">[{{ form.점검연도 }}년, {{ 점검구분표시 }}]</span>
        시설물 외부 점검 사항을 저장하시겠습니까?
      </p>
      <div class="flex justify-end gap-3 mt-6">
        <vs-button type="flat" color="grey" @click="onCancelSave">취소</vs-button>
        <vs-button color="secondary" @click="onConfirmSave">저장</vs-button>
      </div>
    </vs-popup>

    <div class="mt-8">
      <h5 ref="refYeong" class="required"><i class="dot"></i> 점검연도 <number-input v-model="form.점검연도" :useComma="false" :align="'left'" style="width: 114px; display: inline-block" class="mx-2" /><span>년</span></h5>

      <div class="mt-28px">
        <h5 class="mb-4 required"><i class="dot"></i> 점검구분</h5>
        <div ref="refGubun" class="mt-4">
          <div class="flex items-center gap-4">
            <span style="min-width: 70px; font-weight: 500; font-size: 14px;">정기점검 :</span>
            <vs-checkbox :value="form.정기점검 === '상반기'" @input="form.정기점검 = $event ? '상반기' : ''" :disabled="board.readonly">상반기</vs-checkbox>
            <vs-checkbox :value="form.정기점검 === '하반기'" @input="form.정기점검 = $event ? '하반기' : ''" :disabled="board.readonly">하반기</vs-checkbox>
          </div>
          <div class="flex items-center gap-4 mt-2">
            <span style="min-width: 70px; font-weight: 500; font-size: 14px;">수시점검 :</span>
            <vs-checkbox :value="form.수시점검 === '해빙기'" @input="form.수시점검 = $event ? '해빙기' : ''" :disabled="board.readonly">해빙기</vs-checkbox>
            <vs-checkbox :value="form.수시점검 === '우수기'" @input="form.수시점검 = $event ? '우수기' : ''" :disabled="board.readonly">우수기</vs-checkbox>
            <vs-checkbox :value="form.수시점검 === '동절기'" @input="form.수시점검 = $event ? '동절기' : ''" :disabled="board.readonly">동절기</vs-checkbox>
            <vs-checkbox :value="form.수시점검 === '기타'" @input="form.수시점검 = $event ? '기타' : ''" :disabled="board.readonly">기타</vs-checkbox>
          </div>
        </div>
      </div>
    </div>

    <!-- 작성자 정보 -->
    <field-select-module
      v-model="form.stdInfo"
      :visible-field-form="false"
      :use-field-info="false"
      :board="this.board"
      :enabled="true"
      :is-print="isPrint"
      class="mt-28px"
    />

    <!-- 시설 정보 -->
    <div class="mt-28px">
      <h5 class="mb-5"><i class="dot"></i> 시설 정보</h5>
      <div class="writeGridContainer">
        <div ref="refSiseol" class="writeGridLineItem">
          <div class="required">시설명</div>
          <field-selector v-model="form.fields" multiple select-all-active />
        </div>
      </div>
    </div>

    <!-- 시설개요 -->
    <div class="mt-28px">
      <h5 class="mb-5"><i class="dot"></i> 시설개요</h5>
      <div class="table-style">
        <table>
          <colgroup>
            <col width="80" />
          </colgroup>
          <thead style="position: sticky; top: 0; z-index: 10; box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1)">
            <tr>
              <th>연번</th>
              <th>소관부서</th>
              <th>담당자</th>
              <th>구분</th>
              <th>시설구분</th>
              <th>시설명</th>
              <th>시설위치<br />(사업장주소)</th>
              <th>관리방법</th>
              <th>면적(㎡), 형식, 연장, 차로수, 높이 등</th>
              <th>비고</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(field, index) in form.procFields" :key="`tb-${index}`">
              <td>{{ index + 1 }}</td>
              <td>{{ field.소관부서 }}</td>
              <td>{{ field.담당자 }}</td>
              <td>{{ field.구분 }}</td>
              <td>{{ field.시설구분 }}</td>
              <td>{{ field.시설명 }}</td>
              <td>{{ field.시설위치 }}</td>
              <td>{{ field.관리방법 }}</td>
              <td>{{ field.면적형식연장등 }}</td>
              <td>{{ field.비고 }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 점검정보 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 점검정보</h5>
      <div class="writeGridContainer">
        <div ref="refJeomgumil">
          <div class="required">점검일</div>
          <date-selector :readonly="board.readonly" v-model="form.점검일" />
        </div>
        <div ref="refJeomgumjangso">
          <div class="required">점검장소</div>
          <ml-input :readonly="board.readonly" v-model="form.점검장소" />
        </div>
      </div>
    </div>

    <!-- 장비정보 -->
    <div ref="refEquipment">
      <table-equipment
        class="mt-5"
        :list="form.equipmentList"
        @updateList="updateList"
        :isPrint="isPrint"
        :isReadonly="board.readonly"
        :show-none="true"
        :none-value="form.equipmentList_해당사항없음"
        @none-change="form.equipmentList_해당사항없음 = $event"
      ></table-equipment>
    </div>

    <!-- 시설물 외부 작업 -->
    <div ref="refInspect" class="mt-5">
      <vs-row class="mt-8">
        <h5 class="required"><i class="dot"></i> {{ form.inspectionTitle }} 작업</h5>
      </vs-row>
      <table-inspect :is-print="isPrint" :is-readonly="board.readonly" :is-show-title="false" :title="form.inspectionTitle + ' 작업'" :list="form.inspectList" @update-list-check="updateListCheck" />
    </div>

    <!-- 사진대지 -->
    <PhotoUploadGridModule
      ref="refPhoto"
      :row-count="2"
      :grid-count="2"
      v-model="form.photoList"
      :is-print="isPrint"
      :readonly="board.readonly"
      section-title="사진대지"
      placeholder="신축현장 점검"
      showCustom
      :customTitle="true"
    >
      <template #customTitle>
        <h5 class="flex items-center required"><i class="dot"></i> 사진대지</h5>
      </template>
    </PhotoUploadGridModule>

    <!-- 파일첨부 -->
    <file-upload-module ref="refFile" id="fileList" class="w-full mt-4" title="파일첨부" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly">
      <template #titleRight>
        <span style="color: #ff0000; font-weight: 600; margin-left: 2px;">*</span>
      </template>
    </file-upload-module>
  </div>
</template>

<script>
import PopupPlanProject from "@/views/project/popup/PopupPlanProject";
import DateSelector from "@/components/selector/DateSelector";
import PopupProjectSetting from "@/views/project/PopupProjectSetting";
import MemberSelector from "@/components/selector/MemberSelector";
import CompanySelector from "@/components/selector/CompanySelector";
import FileUploader from "@/components/FileUploader";
import FileUploadModule from "@/views/modules/FileUploadModule";
import TableInspect from "@/components/TableInspect";
import TableEquipment from "@/components/TableEquipment";
import DisasterClassSelector from "@/components/selector/DisasterClassSelector";
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import PhotoUploadGridModule from "@/views/modules/PhotoUploadGridModule";
import FieldSelector from "@/components/selector/FieldSelector.vue";
import NumberInput from "@/components/NumberInput.vue";

export default {
  components: {
    FieldSelector,
    PhotoUploadGridModule,
    FieldSelectModule,
    FileUploadModule,
    TableInspect,
    TableEquipment,
    DisasterClassSelector,
    FileUploader,
    CompanySelector,
    MemberSelector,
    PopupProjectSetting,
    DateSelector,
    PopupPlanProject,
    NumberInput,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  computed: {
    점검구분표시() {
      const parts = [];
      if (this.form.정기점검) parts.push(`정기점검 ${this.form.정기점검}`);
      if (this.form.수시점검) parts.push(`수시점검 ${this.form.수시점검}`);
      return parts.join(', ');
    },
    projectId() {
      return this.$route.params.proj_id;
    },
    projectMode() {
      return parseInt(this.projectId) === 5;
    },
    codes() {
      return { ...this.공중이용시설Codes, ...this.원료제조물Codes };
    },
    inspectTable() {
      return [
        { id: 1, label: "각종 균열 및 철근 노출 여부", is_checked: "" },
        { id: 2, label: "옥상 및 지붕 위 물건 적치 등 과하중 상태", is_checked: "" },
        { id: 3, label: "지반 침하 등에 따른 구조물 위험 여부", is_checked: "" },
        { id: 4, label: "외벽 기울기 및 누수 발생 여부", is_checked: "" },
        { id: 5, label: "집수정 배관 리크 상태확인", is_checked: "" },
        { id: 6, label: "집수정 펌프동작 여부상태", is_checked: "" },
        { id: 7, label: "집수정 수위계 상태", is_checked: "" },
      ];
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            if (key === "정기점검" || key === "수시점검") {
              const v = value[key];
              this.form[key] = Array.isArray(v) ? (v[0] || "") : (v || "");
            } else {
              this.form[key] = value[key];
            }
          }
        }

        this.form.inspectionTitle = this.inspectionTitle;
        if (!this.form || !this.form.inspectList || this.form.inspectList.length === 0) {
          this.form.inspectList = this.inspectTable;
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
    "form.fields": {
      immediate: true,
      deep: true,
      handler(fields) {
        this.form.procFields = this.buildProcFields(fields);
      },
    },
  },
  data() {
    return {
      inspectionTitle: "시설물 외부",
      showSaveConfirm: false,
      _saveResolve: null,
      form: {
        점검연도: new Date().getFullYear(),
        정기점검: "",
        수시점검: "",

        inspectionTitle: "시설물 외부",
        stdInfo: {},
        photoList: [],
        fileList: [],
        equipmentList: [],
        equipmentList_해당사항없음: false,
        inspectList: [],
        fields: [],
        procFields: [],
        점검일: "",
        점검장소: "",
      },
      공중이용시설Codes: {
        도서관: "library", "박물관 및 미술관": "museum", 의료기관: "medical", 어린이집: "daycare",
        업무시설: "office", 실내공연장: "indoor-auditorium", 둘이상용도건축물: "mixed-use-building",
        건축물: "building", "항만(방파제)": "port", "항만(부두)": "port-dock", 교량: "bridge",
        터널: "tunnel", 옹벽: "retaining-wall", 절토사면: "cutting-slope", 제방: "dike",
        소규모수도: "waterworks", 청사: "government-building", 복합시설: "complex-facility",
        하수도: "sewage-treatment", 상수도: "water-supply", 실내체육시설: "indoor-sports-facility",
      },
      원료제조물Codes: {
        급식소: "mass_feeding_facility", 먹는물: "drinking-water", 승강기: "lift",
        식품제조가공업: "food-processing", "어린이 놀이시설": "kids-play-area",
      },
    };
  },
  methods: {
    updateList(list) {
      this.form.equipmentList = list;
    },
    updateListCheck(indexRows, text) {
      this.form.inspectList[indexRows].is_checked = text;
    },
    buildProcFields(fields) {
      const list = Array.isArray(fields) ? fields : [];
      return list.map((item) => {
        const meta = this.parseMeta(item.meta);
        const 종별 = (meta.facility_category && meta.facility_category.classification) || "기타";
        return {
          소관부서: meta.소관부서 || "-", 담당자: meta.담당주무관 || "-",
          구분: this.get시설분류(meta) || "-", 시설구분: this.get시설구분(meta, 종별) || "-",
          시설명: item.field_name || "-", 시설위치: item.address || "-",
          관리방법: meta.관리방법 || "-", 면적형식연장등: this.get면적형식연장등(meta) || "-",
          비고: meta.remarks || "-",
        };
      });
    },
    parseMeta(meta) {
      if (!meta) return {};
      if (typeof meta === "object") return meta;
      try { return JSON.parse(meta); } catch (e) { return {}; }
    },
    getFieldTypeToString(type) {
      const keys = Object.keys(this.codes);
      for (const key of keys) { if (this.codes[key] === type) return key; }
    },
    get시설분류(data) {
      if (Object.values(this.공중이용시설Codes).includes(data.field_type)) return "공중이용시설";
      if (Object.values(this.원료제조물Codes).includes(data.field_type)) return "원료·제조물";
      return "기타";
    },
    get시설구분(data, 종별) {
      const n = this.getFieldTypeToString(data.field_type);
      const cls = data.facility_category && data.facility_category.classification || "";
      const cat2 = data.facility_category && data.facility_category.cat2 || "";
      switch (n) {
        case "건축물": return "32-건축물";
        case "교량": return cat2.includes("도로교량") ? ({ "1종": "25-1종 교량", "2종": "25-2종 교량", "3종": "98-3종 교량" }[cls] || "교량") : cat2.includes("철도") ? ({ "1종": "26-1종 교량", "2종": "26-2종 교량", "3종": "100-3종 교량" }[cls] || "교량") : "교량";
        case "도서관": return "7-도서관";
        case "둘이상용도건축물": return "17-둘이상용도건축물";
        case "박물관 및 미술관": return "8-박물관 및 미술관";
        case "복합시설": return "17-복합시설";
        case "상수도": return "36-상수도";
        case "수도": case "소규모수도": return "89-수도법";
        case "실내체육시설": return "19-실내 체육시설";
        case "실내공연장": return "18-실내 공연장";
        case "어린이집": return "11-어린이 집";
        case "업무시설": return "16-업무시설";
        case "옹벽": return { "1종": "38-1종 옹벽", "2종": "38-2종 옹벽", "3종": "38-3종 옹벽" }[종별] || "38-옹벽";
        case "의료기관": return "9-의료기관";
        case "절토사면": return { "1종": "38-1종 절토사면", "2종": "38-2종 절토사면", "3종": "38-3종 절토사면" }[종별] || "38-절토사면";
        case "제방": return "34-제방";
        case "청사": return "16-청사";
        case "터널": return cat2.includes("도로") ? ({ "1종": "27-1종 터널", "2종": "27-2종 터널", "3종": "99-3종 터널" }[cls] || "터널") : cat2.includes("철도") ? ({ "1종": "28-1종 터널", "2종": "28-2종 터널", "3종": "101-3종 터널" }[cls] || "터널") : "터널";
        case "하수도": return "37-하수도";
        case "항만(방파제)": return "29-방파제";
        case "항만(부두)": return "30-부두";
        case "급식소": return "69-식품, 식품첨가물, 기구 및 용기포장(집단급식소)";
        case "먹는물": return "90-먹는물 관리법";
        case "승강기": return "85-승강기 안전관리법";
        case "식품제조가공업": return "69-식품제조가공업";
        case "어린이 놀이시설": return "84-어린이 놀이시설 안전관리법";
        default: return `${n} - ${종별}`;
      }
    },
    get면적형식연장등(data) {
      const m = this.parseMeta(data.meta) || data;
      let r = "";
      if (m.건축_연면적) r += `건축 연면적: ${m.건축_연면적}, `;
      if (m.층수_최고높이) r += `층수-최고높이: ${m.층수_최고높이}, `;
      if (m.연장_길이) r += `연장-길이: ${m.연장_길이}, `;
      if (m.차로수_상행) r += `차로수-상행: ${m.차로수_상행}, `;
      if (m.차로수_하행) r += `차로수-하행: ${m.차로수_하행}, `;
      if (m.시설용량) r += `시설용량: ${m.시설용량}, `;
      if (m.경간형식) r += `경간형식: ${m.경간형식}, `;
      if (m.연장) r += `연장: ${m.연장}, `;
      if (m.높이) r += `높이: ${m.높이}, `;
      if (m.주요공법) r += `주요공법: ${m.주요공법}, `;
      if (m.일평균) r += `일평균: ${m.일평균}, `;
      if (m.제방연장) r += `제방연장: ${m.제방연장}, `;
      if (m.종류) r += `종류: ${m.종류}, `;
      if (m.허용월파량_호안) r += `허용월파량(호안): ${m.허용월파량_호안}, `;
      if (m.대표형식) r += `대표형식: ${m.대표형식}, `;
      if (m.일평균_식수인원) r += `일평균 식수인원: ${m.일평균_식수인원}, `;
      if (m.기타_상세제원 || m.otherSpecifications) r += `기타 상세제원: ${m.기타_상세제원 || m.otherSpecifications}, `;
      r = r.slice(0, -2);
      return r.length === 0 ? "-" : r;
    },

    validate(options = {}) {
      if (options && options.isDraft) {
        return true;
      }

      const getEl = (refName) => {
        const ref = this.$refs[refName];
        if (!ref) return null;
        return ref.$el || ref;
      };

      const scrollTo = (refName) => () => {
        const el = getEl(refName);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(function() {
          const focusable = el.querySelector('input:not([type="hidden"]), textarea');
          if (focusable) focusable.focus();
        }, 400);
      };

      const scrollAndClick = (refName) => () => {
        const el = getEl(refName);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(function() {
          const clickable = el.querySelector('input:not([type="hidden"]), [role="combobox"], button, .vs-select--input');
          if (clickable) clickable.click();
          else el.click();
        }, 400);
      };

      const messages = [];

      if (!this.form.점검연도) {
        messages.push({ text: '[연도]를 입력하여 주십시오.', action: scrollTo('refYeong') });
      }

      if (!this.form.정기점검 && !this.form.수시점검) {
        messages.push({ text: '[점검구분]의 해당 점검에 체크하여 주십시오.', action: scrollTo('refGubun') });
      }

      if (!this.form.fields || this.form.fields.length === 0) {
        messages.push({ text: '[시설정보]의 "시설명"을 입력하여 주십시오.', action: scrollAndClick('refSiseol') });
      }

      if (!this.form.점검일) {
        messages.push({ text: '[점검정보]의 "점검일"을 선택하여 주십시오.', action: scrollAndClick('refJeomgumil') });
      }

      if (!this.form.점검장소 || String(this.form.점검장소).trim() === '') {
        messages.push({ text: '[점검정보]의 "점검장소"를 입력하여 주십시오.', action: scrollTo('refJeomgumjangso') });
      }

      const 장비있음 = Array.isArray(this.form.equipmentList) && this.form.equipmentList.some(function(row) {
        return row && (
          (row.name && String(row.name).trim() !== '') ||
          (row.manufacturer && String(row.manufacturer).trim() !== '') ||
          (row.standard && String(row.standard).trim() !== '')
        );
      });
      if (!장비있음 && !this.form.equipmentList_해당사항없음) {
        messages.push({
          text: '[장비 정보]를 입력하여 주십시오.',
          sub: "해당사항이 없을 경우 '해당사항 없음'에 체크하여 주십시오.",
          action: scrollTo('refEquipment'),
        });
      }

      const 미체크항목있음 = Array.isArray(this.form.inspectList) && this.form.inspectList.some(function(row) {
        return !row || !row.is_checked || String(row.is_checked).trim() === '';
      });
      if (미체크항목있음) {
        messages.push({ text: '[시설물 외부 작업]의 "점검내용"에 체크되지 않은 항목이 있습니다.', action: scrollTo('refInspect') });
      }

      const 사진있음 = Array.isArray(this.form.photoList) && this.form.photoList.some(function(p) { return p && p.id; });
      if (!사진있음) {
        messages.push({ text: '[사진대지] 이미지를 업로드하여 주십시오.', action: scrollTo('refPhoto') });
      }

      if (!this.form.fileList || this.form.fileList.length === 0) {
        messages.push({ text: '[파일첨부]점검 관련 파일을 첨부하여 주십시오.', action: scrollTo('refFile') });
      }

      if (messages.length > 0) {
        window.dispatchEvent(new CustomEvent("requiredFieldAlert", { detail: { messages } }));
        return false;
      }

      return new Promise((resolve) => {
        this._saveResolve = resolve;
        this.showSaveConfirm = true;
      });
    },

    onConfirmSave() {
      this.showSaveConfirm = false;
      if (this._saveResolve) {
        this._saveResolve(true);
        this._saveResolve = null;
      }
    },

    onCancelSave() {
      this.showSaveConfirm = false;
      if (this._saveResolve) {
        this._saveResolve(false);
        this._saveResolve = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
$border: 1px solid #cdcdcd;
.label-text {
  width: 114px;
  font-weight: 500;
  font-size: 14px;
}
.mt-component-form {
  margin-top: 15px;
}

/* uploaded File */
.h-upload {
  height: 470px;
}
.upload-content {
  border: $border;
  &:last-child {
    border-left: none;
  }
  &__title {
    height: 54px;
    line-height: 54px;
    text-align: center;
    background: #fafafa;
    border-bottom: $border;
  }
  &__input {
    height: 54px;
    background: #f9f9f9;
    border-top: $border;
    padding: 5px 15px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__file {
    height: calc(100% - 54px);
    width: 100%;
    padding: 14px;
  }
}

.table-style {
  height: 300px;
  background-color: #fafafa;
  overflow-y: auto;

  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;

    th, td {
      border: 1px solid #cdcdcd;
      border-left: 0;
      border-right: 0;
    }

    thead th {
      font-weight: 700;
      font-size: 14px;
      text-align: center;
      padding: 16px 0;
      background-color: #fafafa;
    }

    td {
      font-weight: 400;
      font-size: 14px;
      text-align: center;
      padding: 10px 0;
      background-color: white;
    }
  }
}

.save-confirm-text {
  font-size: 15px;
  line-height: 1.6;
}

.save-confirm-highlight {
  color: #7e72f2;
  font-weight: 600;
}

.required-notice {
  border-radius: 5px;
  border: 1px solid #cdcdcd;
  padding: 12px 16px;
  font-size: 16px;
  color: #2C2C2C;
  font-weight: 700;

  &::before {
    content: "* ";
    color: #ff0000;
    font-weight: 600;
  }
}

.required::before {
  content: "*";
  color: #ff0000;
  font-weight: 600;
  margin-right: 4px;
}
</style>
